import { useState, useRef, useCallback } from 'react';
import * as tus from 'tus-js-client';
import { api } from '../lib/api';

interface VideoUploaderProps {
  onUploadComplete?: (entryId: string) => void;
  onError?: (error: Error) => void;
}

export function VideoUploader({ onUploadComplete, onError }: VideoUploaderProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    // Validate file type
    if (!file.type.startsWith('video/') && !file.type.startsWith('audio/')) {
      onError?.(new Error(`Invalid file type: ${file.type}. Please select a video or audio file.`));
      return;
    }

    // Validate file size (1GB limit)
    const maxSize = 1 * 1024 * 1024 * 1024;
    if (file.size > maxSize) {
      onError?.(new Error(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum size is 1GB.`));
      return;
    }

    setSelectedFile(file);
  }, [onError]);

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Request upload URL
      setUploadProgress(10);
      const response = await api.requestUploadUrl({
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type,
        title: selectedFile.name.replace(/\.[^/.]+$/, ''), // Remove extension
        createdAt: selectedFile.lastModified, // Use file's actual modification timestamp
      });

      const { entryId, uploadUrl, streamUid } = response;

      // Step 2: Upload file (Stream TUS or R2 depending on streamUid presence)
      if (streamUid) {
        // Video file - upload to Cloudflare Stream via TUS protocol
        await uploadToStream(uploadUrl, selectedFile, (percent) => {
          // Map 0-100% upload to 10-80% total progress
          const mappedProgress = 10 + (percent * 0.7);
          setUploadProgress(Math.round(mappedProgress));
        });
      } else {
        // Audio file - upload to R2 via presigned URL
        await api.uploadDirect(uploadUrl, selectedFile, (percent) => {
          // Map 0-100% upload to 10-80% total progress
          const mappedProgress = 10 + (percent * 0.7);
          setUploadProgress(Math.round(mappedProgress));
        });
      }

      // Step 3: Get media duration
      setUploadProgress(80);
      const duration = await getMediaDuration(selectedFile);

      // Step 4: Confirm upload completion
      setUploadProgress(90);
      await api.confirmUpload({
        entryId,
        duration,
      });

      setUploadProgress(100);
      setSelectedFile(null);
      onUploadComplete?.(entryId);
    } catch (error) {
      console.error('Upload failed:', error);
      onError?.(error instanceof Error ? error : new Error('Upload failed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const uploadToStream = (
    workerProxyURL: string,
    file: File,
    onProgress: (percent: number) => void
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const upload = new tus.Upload(file, {
        uploadUrl: workerProxyURL, // Resume upload via Worker proxy (upload already created)
        chunkSize: 52_428_800, // 50MB chunks (divisible by 256 KiB)
        retryDelays: [0, 3000, 5000, 10000, 20000],

        onProgress(bytesUploaded, bytesTotal) {
          const percentage = (bytesUploaded / bytesTotal) * 100;
          onProgress(percentage);
        },

        onSuccess() {
          console.log('TUS upload complete');
          resolve();
        },

        onError(error) {
          console.error('TUS upload error:', error);
          reject(error);
        },
      });

      upload.start();
    });
  };

  const getMediaDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const media = document.createElement(file.type.startsWith('video/') ? 'video' : 'audio');

      media.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(Math.round(media.duration));
      };

      media.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to read media duration'));
      };

      media.src = url;
    });
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="video-uploader">
      <div
        className={`upload-area ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*,audio/*"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        {selectedFile ? (
          <div className="file-selected">
            <p className="file-name">{selectedFile.name}</p>
            <p className="file-size">
              {(selectedFile.size / 1024 / 1024).toFixed(2)}MB
            </p>
            {!uploading && (
              <button onClick={(e) => { e.stopPropagation(); handleUpload(); }} className="upload-button">
                Upload
              </button>
            )}
          </div>
        ) : (
          <div className="upload-prompt">
            <p>Drag and drop a video or audio file here</p>
            <p>or click to browse</p>
            <p className="file-limit">Maximum file size: 1GB</p>
          </div>
        )}
      </div>

      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
          </div>
          <p className="progress-text">{uploadProgress}% - Uploading...</p>
        </div>
      )}

      <style>{`
        .video-uploader {
          width: 100%;
          max-width: 600px;
          margin: 0 auto;
        }

        .upload-area {
          border: 2px dashed #ccc;
          border-radius: 8px;
          padding: 40px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .upload-area:hover {
          border-color: #007bff;
          background-color: #f8f9fa;
        }

        .upload-area.dragging {
          border-color: #28a745;
          background-color: #e8f5e9;
        }

        .upload-prompt {
          color: #666;
        }

        .upload-prompt p {
          margin: 8px 0;
        }

        .file-limit {
          font-size: 0.875rem;
          color: #999;
          margin-top: 12px !important;
        }

        .file-selected {
          padding: 20px;
        }

        .file-name {
          font-weight: 600;
          margin-bottom: 8px;
          word-break: break-word;
        }

        .file-size {
          color: #666;
          font-size: 0.875rem;
          margin-bottom: 16px;
        }

        .upload-button {
          padding: 12px 32px;
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .upload-button:hover {
          background-color: #0056b3;
        }

        .upload-progress {
          margin-top: 20px;
        }

        .progress-bar {
          width: 100%;
          height: 8px;
          background-color: #e9ecef;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background-color: #007bff;
          transition: width 0.3s ease;
        }

        .progress-text {
          text-align: center;
          margin-top: 8px;
          color: #666;
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
}
